import * as React from 'react';
import { createStyles, Button, Group, Drawer, Badge, Tooltip } from '@mantine/core';
import { useDisclosure } from '@mantine/hooks';
import { NavLink } from 'react-router-dom';
import { ColumnDef } from '@tanstack/react-table';
import { useTranslation } from 'react-i18next';
import { useQuery } from '@tanstack/react-query';
import { IconPlus } from '@tabler/icons-react';

// API
import { GetHasActiveSurvey, queryClient } from '../../api';
import { useOrganization } from '../../api/hooks/useOrganization';

// Components
import { TablePaginated } from '../Table/TablePaginated';
import { TableStatic } from '../Table/TableStatic';
import { ContactForm, ContactFormHandle } from '../ContactForm';
import { fetchPaginatedContactsData, fetchOrganizationContactsData } from './helpers';

// Contexts
import { AuthContext } from '../../contexts/Auth';

// Hooks
import { useDeviceIdentification } from '../../hooks/useDeviceIdentification';

// Helpers
import { formatPhoneNumber } from '../../helpers/formatters';

// Types
import { IContactRecord, TableQueryParams } from '../../types';

const useStyles = createStyles((theme) => ({
    drawerHeader: {
        zIndex: 1
    },
    drawerInner: {
        left: 0
    }
}));

interface ContactsTableProps {
    clientId?: string;
    openLinkPrefix: string;
    openInNewTab?: boolean;
    createContact?: boolean;
}

const defaultSort = 'name';

export const ContactsTable: React.FC<ContactsTableProps> = ({
    clientId,
    openInNewTab,
    createContact,
    openLinkPrefix
}) => {
    const ContactFormRef = React.useRef<ContactFormHandle>(null);
    const { authState } = React.useContext(AuthContext);
    const { data: orgData, isLoading } = useOrganization({ clientId: clientId });
    const { classes } = useStyles();
    const [drawerOpened, { open: openDrawer, close: closeDrawer }] = useDisclosure(false);
    const { t: columnHeaderTrans } = useTranslation('components', { keyPrefix: 'tables.contacts.columns' });
    const { t } = useTranslation('common');
    const { data: hasActiveSurvey } = useQuery({
        queryKey: ['clientHasActiveSurvey', clientId],
        queryFn: async () => {
            if (clientId) {
                const data = await GetHasActiveSurvey(clientId);
                return data;
            } else {
                return false;
            }
        },
        enabled: Boolean(clientId?.length),
        refetchOnWindowFocus: false
    });
    const rightControl = React.useMemo(() => {
        return createContact ? (
            <Button
                variant="default"
                onClick={openDrawer}
                leftIcon={<IconPlus size={14} />}
                disabled={hasActiveSurvey === true}
            >
                {t('contactsModule.newContact', { ns: 'components' })}
            </Button>
        ) : null;
    }, [createContact, hasActiveSurvey]);
    const columns = React.useMemo<ColumnDef<IContactRecord>[]>(
        () => [
            {
                accessorFn: (row) => row?.name?.fullName,
                id: 'name',
                cell: (info) => info.getValue() ?? '--',
                footer: (props) => props.column.id,
                header: () => columnHeaderTrans('name'),
                enableColumnFilter: false,
                meta: {
                    searchable: true
                }
            },
            {
                accessorFn: (row) => row.organization,
                id: 'organization',
                cell: (props) => {
                    const { _id = '', name = '--' } = props.row.original.organization ?? {};

                    return (
                        <Group style={{ flexWrap: 'nowrap' }}>
                            <NavLink to={`/admin/clients/clientId/${_id}`}>{name}</NavLink>
                        </Group>
                    );
                },
                header: () => columnHeaderTrans('organization'),
                footer: (props) => props.column.id,
                enableColumnFilter: false
            },
            {
                accessorKey: 'phone',
                header: () => columnHeaderTrans('primaryPhone'),
                cell: (props) => {
                    const { primary = {} } = props.row.original.phone ?? {};

                    return formatPhoneNumber(primary);
                },
                footer: (props) => props.column.id,
                enableColumnFilter: false
            },
            {
                accessorFn: (row) => row?.email?.primary,
                id: 'email',
                cell: (info) => info.getValue() ?? '--',
                header: () => columnHeaderTrans('primaryEmail'),
                footer: (props) => props.column.id,
                enableColumnFilter: false,
                meta: {
                    searchable: true
                }
            },
            {
                accessorKey: 'isOkayToContact',
                header: () => columnHeaderTrans('isOkayToContact'),
                footer: (props) => props.column.id,
                cell: (info) => t(info.getValue() ? 'yes' : 'no', { ns: 'common' }),
                enableColumnFilter: true,
                meta: clientId
                    ? {}
                    : {
                          filterDataSetKey: 'YES_OR_NO_BINARY'
                      },
                filterFn: clientId ? 'fuzzy' : 'multi'
            },
            {
                id: 'actions',
                enableColumnFilter: false,
                cell: (props) => {
                    const isSelf = authState?.contactId === props.row.original.id;

                    return (
                        <Group position="right" style={{ flexWrap: 'nowrap' }}>
                            {isSelf ? (
                                <Tooltip label={t('yourContact', { ns: 'common' })} withArrow>
                                    <Badge variant="outline" color="orange">
                                        {t('you', { ns: 'common' })}
                                    </Badge>
                                </Tooltip>
                            ) : null}
                            <NavLink
                                to={`${openLinkPrefix}contact/${props.row.original.id}`}
                                {...(openInNewTab
                                    ? {
                                          rel: 'noopener noreferrer',
                                          target: '_blank'
                                      }
                                    : {})}
                            >
                                <Button>{t('open', { ns: 'common' })}</Button>
                            </NavLink>
                        </Group>
                    );
                }
            }
        ],
        [authState?.contactId]
    );
    const closeContactForm = () => {
        closeDrawer();
    };
    const highlightRow = React.useCallback(
        (row: any) => {
            const rowData = row as IContactRecord;

            return rowData.id === authState?.contactId;
        },
        [authState]
    );

    return (
        <>
            {clientId ? (
                <OrganizationContactsTable {...{ columns, rightControl, defaultSort, highlightRow, clientId }} />
            ) : (
                <PaginatedContactsTable {...{ columns, rightControl, defaultSort, highlightRow }} />
            )}
            <Drawer
                opened={drawerOpened}
                onClose={() => {
                    if (typeof ContactFormRef?.current?.onCancel === 'function') {
                        ContactFormRef.current.onCancel();
                    } else {
                        closeDrawer();
                    }
                }}
                title={t('contactsModule.newContact', { ns: 'components' })}
                position="right"
                size="xl"
                overlayProps={{ opacity: 0.5, blur: 4 }}
                classNames={{
                    header: classes.drawerHeader,
                    inner: classes.drawerInner
                }}
            >
                <ContactForm
                    ref={ContactFormRef}
                    clientId={clientId}
                    isNew={true}
                    contactId={undefined}
                    onCancel={closeContactForm}
                    ancillaryData={{
                        organization: orgData
                    }}
                    onSave={async () => {
                        await queryClient.invalidateQueries({
                            queryKey: clientId ? ['contactById', clientId] : ['contacts'],
                            exact: false,
                            refetchType: 'active'
                        });
                        if (clientId) {
                            await queryClient.invalidateQueries({
                                queryKey: ['clientById', clientId],
                                exact: true,
                                refetchType: 'active'
                            });
                            await queryClient.invalidateQueries({
                                queryKey: ['clientRecordById', clientId],
                                exact: true,
                                refetchType: 'active'
                            });
                        }
                        closeContactForm();
                    }}
                />
            </Drawer>
        </>
    );
};

interface OrganizationContactsTableProps extends Omit<ContactsTableProps, 'openLinkPrefix'> {
    columns: ColumnDef<IContactRecord, any>[];
    rightControl?: React.ReactNode;
    highlightRow?: (args: any) => boolean;
    defaultSort: string;
}

export const OrganizationContactsTable: React.FC<OrganizationContactsTableProps> = ({
    clientId,
    columns,
    rightControl,
    highlightRow,
    defaultSort
}) => {
    const { data } = useQuery(['contactsById', clientId], () => fetchOrganizationContactsData({ clientId }));
    const { isDesktop, isDesktopWide } = useDeviceIdentification();

    return (
        <TableStatic<IContactRecord>
            data={data?.rows}
            visibleColumns={
                clientId
                    ? {
                          organization: false,
                          isOkayToContact: isDesktopWide,
                          phone: isDesktop || isDesktopWide
                      }
                    : {}
            }
            columns={columns}
            searchable
            noMargin
            enableColumnFilters
            rightControl={rightControl}
            highlightRow={highlightRow}
            defaultSort={defaultSort}
        />
    );
};

interface PaginatedContactsTableProps {
    columns: ColumnDef<IContactRecord, any>[];
    rightControl?: React.ReactNode;
    highlightRow?: (args: any) => boolean;
    defaultSort: string;
}

export const PaginatedContactsTable: React.FC<PaginatedContactsTableProps> = ({
    columns,
    rightControl,
    highlightRow,
    defaultSort
}) => {
    const { isDesktop, isDesktopWide } = useDeviceIdentification();

    return (
        <TablePaginated<IContactRecord>
            queryKey={`contacts`}
            queryCb={(options: TableQueryParams) => fetchPaginatedContactsData({ options })}
            columns={columns}
            visibleColumns={{
                isOkayToContact: isDesktopWide,
                phone: isDesktop || isDesktopWide,
                email: isDesktop || isDesktopWide
            }}
            searchable
            noMargin
            enableColumnFilters
            rightControl={rightControl}
            highlightRow={highlightRow}
            defaultSort={defaultSort}
        />
    );
};
